package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.domain.budget.LabourInclusionPeriod
import com.evoapps.evoshift.domain.repository.LabourInclusionRepository
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.serialization.Serializable

/** SH-206: one period of a person's setting: from [fromMonth] ("YYYY-MM") on, [counts]. */
@Serializable
internal data class LabourInclusionPeriodDto(val fromMonth: String = "", val counts: Boolean = true)

/**
 * `workplaces/{id}/labour_inclusion/{uid}` (SH-206): whether the person counts in the workplace's
 * labour cost, labour % and SPMH, month by month. firestore.rules (validLabourInclusion) accept
 * exactly these fields, from the workplace's owner and financial managers.
 */
@Serializable
internal data class LabourInclusionDto(
    val userId: String = "",
    val periods: List<LabourInclusionPeriodDto> = emptyList(),
    val updatedAt: String? = null,
    val updatedBy: String? = null
)

/** The periods kept: well-formed months only, oldest first. */
internal fun LabourInclusionDto.toDomain(): List<LabourInclusionPeriod> =
    periods.filter { isMonth(it.fromMonth) }.map { LabourInclusionPeriod(it.fromMonth, it.counts) }.sortedBy { it.fromMonth }

/** "YYYY-MM". */
private fun isMonth(value: String): Boolean =
    value.length == 7 && value[4] == '-' && value.withIndex().all { (i, c) -> i == 4 || c.isDigit() }

class LabourInclusionRepositoryImpl(private val firestore: AppFirestore) : LabourInclusionRepository {
    private fun collection(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("labour_inclusion")

    override suspend fun getAll(workplaceId: String): Result<Map<String, List<LabourInclusionPeriod>>> = runCatching {
        collection(workplaceId).get().documents.mapNotNull { doc ->
            runCatching { doc.id to doc.data<LabourInclusionDto>().toDomain() }.getOrNull()
        }.filter { it.second.isNotEmpty() }.toMap()
    }

    override suspend fun get(workplaceId: String, userId: String): Result<List<LabourInclusionPeriod>> = runCatching {
        val snapshot = collection(workplaceId).document(userId).get()
        if (!snapshot.exists) emptyList() else snapshot.data<LabourInclusionDto>().toDomain()
    }

    override suspend fun save(workplaceId: String, userId: String, periods: List<LabourInclusionPeriod>, updatedBy: String): Result<Unit> = runCatching {
        val ref = collection(workplaceId).document(userId)
        if (periods.isEmpty()) {
            ref.delete()
        } else {
            ref.set(
                LabourInclusionDto(
                    userId = userId,
                    periods = periods.map { LabourInclusionPeriodDto(it.fromMonth, it.counts) },
                    updatedAt = AppClock.now().toString(),
                    updatedBy = updatedBy
                )
            )
        }
    }
}
